/**
 * Centralized API Utility Module
 *
 * All authenticated API calls should use these functions to ensure
 * consistent JWT token injection and request formatting.
 *
 * SECURITY FIX: Added global Axios interceptor for 401 handling.
 * JWT token is retrieved from Redux store (persisted in localStorage).
 */
import axios from 'axios';
import { store } from '../store';
import { getLoginObjDetails } from '../login/login.selector';
import AlertManager from '../../utils/alertManager';

/**
 * Global Axios Response Interceptor
 *
 * SECURITY FIX: Centralized 401 handling instead of checking in every saga.
 * When any API returns 401 (Unauthorized) or a custom statusCode '401',
 * the token validation alert is shown globally.
 */
axios.interceptors.response.use(
	(response) => {
		// Check for custom 401 statusCode in response body (backend convention)
		if (response.data?.statusCode === '401') {
			AlertManager.showTokenValidationAlert();
		}
		return response;
	},
	(error) => {
		// Handle HTTP 401 status code (standard unauthorized)
		if (error.response?.status === 401) {
			AlertManager.showTokenValidationAlert();
		}
		return Promise.reject(error);
	},
);

/**
 * Makes an authenticated API call with JSON content type.
 * Automatically attaches the JWT Bearer token from the Redux store.
 * @param url - The full API endpoint URL
 * @param data - The request payload (will be sent as JSON body)
 * @param method - HTTP method (default: 'POST')
 * @returns Promise resolving to the Axios response
 */
export const makeApiCall = async (
	url: string,
	data: any,
	method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST',
) => {
	const state = store.getState();
	const token = getLoginObjDetails(state)?.jwtToken || null;

	const config = {
		method,
		url,
		data,
		headers: {
			'Content-type': 'application/json',
			Authorization: `Bearer ${token}`,
		},
	};

	return axios(config);
};

/**
 * Makes an authenticated API call with form-encoded content type.
 * Used for endpoints that expect URL-encoded form data instead of JSON.
 * @param url - The full API endpoint URL
 * @param params - URLSearchParams object containing form data
 * @param method - HTTP method (default: 'POST')
 * @returns Promise resolving to the Axios response
 */
export const makeFormApiCall = async (
	url: string,
	params: URLSearchParams,
	method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'POST',
) => {
	const state = store.getState();
	const token = getLoginObjDetails(state)?.jwtToken || null;

	const config = {
		method,
		url,
		data: params,
		headers: {
			'Content-type': 'application/x-www-form-urlencoded',
			Authorization: `Bearer ${token}`,
		},
	};

	return axios(config);
};
